import { useCallback } from 'react';
import { useMutation } from '@apollo/client';
// Api
import {
  ADD_TICKET_EVENT_AFFILIATE,
  REGENERATE_TICKET_EVENT_AFFILIATE_LINK,
  SET_TICKET_EVENT_AFFILIATE_COMMISSION,
  SET_TICKET_EVENT_AFFILIATE_STATUS,
} from 'api/ticketing/mutations';
// Types
import {
  AddTicketEventAffiliate,
  AddTicketEventAffiliateVariables,
} from 'api/ticketing/types/AddTicketEventAffiliate';
import {
  RegenerateTicketEventAffiliateLink,
  RegenerateTicketEventAffiliateLinkVariables,
} from 'api/ticketing/types/RegenerateTicketEventAffiliateLink';
import {
  SetTicketEventAffiliateCommission,
  SetTicketEventAffiliateCommissionVariables,
} from 'api/ticketing/types/SetTicketEventAffiliateCommission';
import {
  SetTicketEventAffiliateStatus,
  SetTicketEventAffiliateStatusVariables,
} from 'api/ticketing/types/SetTicketEventAffiliateStatus';
import { StreamAffiliateLinkStatus } from 'api/graphql-global-types';
// Helpers
import { runTicketingChange } from 'pages/Events/runTicketingChange';

export type AffiliateOwner = {
  ticketEventId?: string;
  streamId?: string | null;
};

type AffiliateLink = { id: string; status: StreamAffiliateLinkStatus };

export type NewAffiliateValues = { userId: string; commissionPercent?: number };

export type AffiliateActions = {
  addAffiliate: (values: NewAffiliateValues) => Promise<boolean>;
  setCommission: (
    linkId: string,
    commissionPercent: number
  ) => Promise<boolean>;
  regenerateLink: (link: AffiliateLink) => Promise<boolean>;
  /** Disables an active link and re-enables a disabled one. */
  toggleLinkStatus: (link: AffiliateLink) => Promise<boolean>;
  adding: boolean;
  savingRate: boolean;
};

/** Affiliate link changes; each one notifies the admin, calls onChanged when it works and resolves to whether it did. */
export const useAffiliateActions = (
  owner: AffiliateOwner,
  onChanged: () => void
): AffiliateActions => {
  const [addMutation, { loading: adding }] = useMutation<
    AddTicketEventAffiliate,
    AddTicketEventAffiliateVariables
  >(ADD_TICKET_EVENT_AFFILIATE);
  const [commissionMutation, { loading: savingRate }] = useMutation<
    SetTicketEventAffiliateCommission,
    SetTicketEventAffiliateCommissionVariables
  >(SET_TICKET_EVENT_AFFILIATE_COMMISSION);
  const [statusMutation] = useMutation<
    SetTicketEventAffiliateStatus,
    SetTicketEventAffiliateStatusVariables
  >(SET_TICKET_EVENT_AFFILIATE_STATUS);
  const [regenerateMutation] = useMutation<
    RegenerateTicketEventAffiliateLink,
    RegenerateTicketEventAffiliateLinkVariables
  >(REGENERATE_TICKET_EVENT_AFFILIATE_LINK);

  const run = useCallback(
    async (
      change: () => Promise<unknown>,
      success: string,
      failure: string
    ): Promise<boolean> => {
      const done = await runTicketingChange(change, success, failure);

      if (done) {
        onChanged();
      }
      return done;
    },
    [onChanged]
  );

  const addAffiliate = useCallback(
    ({ userId, commissionPercent }: NewAffiliateValues): Promise<boolean> =>
      run(
        () =>
          addMutation({
            variables: {
              input: {
                ...owner,
                userId,
                commissionPercent: commissionPercent ?? null,
              },
            },
          }),
        'The affiliate link is ready',
        'Could not add the affiliate'
      ),
    [addMutation, owner, run]
  );

  const setCommission = useCallback(
    (linkId: string, commissionPercent: number): Promise<boolean> =>
      run(
        () =>
          commissionMutation({
            variables: { input: { linkId, commissionPercent } },
          }),
        `Commission set to ${commissionPercent}%`,
        'Could not update the commission rate'
      ),
    [commissionMutation, run]
  );

  const regenerateLink = useCallback(
    (link: AffiliateLink): Promise<boolean> =>
      run(
        () => regenerateMutation({ variables: { input: { linkId: link.id } } }),
        'New affiliate link generated',
        'Could not generate a new link'
      ),
    [regenerateMutation, run]
  );

  const toggleLinkStatus = useCallback(
    (link: AffiliateLink): Promise<boolean> => {
      const active = link.status === StreamAffiliateLinkStatus.Active;

      return run(
        () =>
          statusMutation({
            variables: {
              input: {
                linkId: link.id,
                status: active
                  ? StreamAffiliateLinkStatus.Disabled
                  : StreamAffiliateLinkStatus.Active,
              },
            },
          }),
        active ? 'Affiliate link disabled' : 'Affiliate link re-enabled',
        'Could not update the affiliate link'
      );
    },
    [statusMutation, run]
  );

  return {
    addAffiliate,
    setCommission,
    regenerateLink,
    toggleLinkStatus,
    adding,
    savingRate,
  };
};
